import { Box } from "@chakra-ui/react";
import React from "react";
import { createPortal } from "react-dom";
import { COLUMN_MAX_W } from "src/layouts/AppLayout";

/**
 * A native-style bottom sheet: slides up over a dimmed page, closes on a
 * backdrop tap, Escape, or a downward swipe on the sheet.
 */
export function BottomSheet({
  open,
  onClose,
  children,
}: React.PropsWithChildren<{ open: boolean; onClose: () => void }>) {
  const [dragY, setDragY] = React.useState(0);
  const startY = React.useRef<number | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  React.useEffect(() => {
    if (open) setDragY(0);
  }, [open]);

  if (!open) return null;

  function onTouchStart(e: React.TouchEvent) {
    // Only start a close-drag when the sheet's content is scrolled to the top.
    if ((scrollRef.current?.scrollTop ?? 0) > 0) return;
    startY.current = e.touches[0].clientY;
  }
  function onTouchMove(e: React.TouchEvent) {
    if (startY.current === null) return;
    setDragY(Math.max(0, e.touches[0].clientY - startY.current));
  }
  function onTouchEnd() {
    if (startY.current === null) return;
    startY.current = null;
    if (dragY > 110) onClose();
    else setDragY(0);
  }

  return createPortal(
    <Box position="fixed" inset={0} zIndex={50}>
      <Box
        position="absolute"
        inset={0}
        bg="rgba(0,0,0,0.6)"
        backdropFilter="blur(4px)"
        animation="tlFade 0.2s ease both"
        onClick={onClose}
      />
      <Box
        role="dialog"
        aria-modal="true"
        position="absolute"
        bottom={0}
        left="50%"
        width="100%"
        maxW={COLUMN_MAX_W}
        style={{
          transform: `translateX(-50%) translateY(${dragY}px)`,
          transition: startY.current === null ? "transform 0.25s ease" : "none",
        }}
      >
        <Box
          bg="surface"
          borderTopRadius="28px"
          maxH="92dvh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          animation="tlSheetUp 0.32s cubic-bezier(0.2, 0.9, 0.3, 1) both"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <Box
            position="absolute"
            top={2}
            left="50%"
            transform="translateX(-50%)"
            w="40px"
            h="5px"
            borderRadius="full"
            bg="rgba(255,255,255,0.35)"
            zIndex={2}
          />
          <Box
            ref={scrollRef}
            overflowY="auto"
            pb="env(safe-area-inset-bottom)"
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
}
